import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { Router } from '@angular/router';
import { TimerService, Timer } from '../../../services/timer/timer.service';
import { Subscription } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-multi-timer-panel',
  templateUrl: './multi-timer-panel.component.html',
  styleUrls: ['./multi-timer-panel.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiTimerPanelComponent implements OnInit, OnDestroy {
  roleSlug = 'admin';
  timers: Timer[] = [];
  isCollapsed = false;
  isLoading = false;

  selectedTimer: Timer | null = null;
  showEditTimerModal = false;

  // View mode toggle
  viewMode: 'single' | 'detailed' = 'detailed';
  private readonly VIEW_MODE_KEY = 'pmt_timer_view_mode';

  // Drag optimization
  isDragging = false;

  @Output() onTimerClick = new EventEmitter<string>();
  @Output() onTimerStopped = new EventEmitter<any>();

  private subscriptions: Subscription[] = [];

  constructor(
    private timerService: TimerService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private confirmationService: ConfirmationService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadViewMode();
    this.loadTimers();
    this.subscribeToTimerUpdates();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadTimers(): void {
    this.isLoading = true;
    this.timerService.loadActiveTimers();
  }

  subscribeToTimerUpdates(): void {
    const sub = this.timerService.timers$.subscribe((timers) => {
      if (this.isDragging) return;
      this.timers = timers;
      this.isLoading = false;
      this.cdr.markForCheck();
    });
    this.subscriptions.push(sub);
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  onPauseTimer(timer: Timer, event: Event): void {
    event.stopPropagation();
    this.timerService.pauseTimer(timer.timerId);
  }

  onResumeTimer(timer: Timer, event: Event): void {
    event.stopPropagation();
    this.timerService.resumeTimer(timer.timerId);
  }

  onStopTimer(timer: Timer, event: Event): void {
    event.stopPropagation();
    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - timer.elapsedTime * 1000);
    const stoppedTimerData = {
      taskId: timer.taskId,
      projectId: timer.projectId,
      timerId: timer.timerId,
      task: {
        _id: timer.taskId,
        title: timer.taskName,
        projectId: { _id: timer.projectId, name: timer.projectName },
      },
      initialDuration: timer.elapsedTime,
      startTime,
      endTime,
    };
    this.timerService.pauseTimerAndOpenTimeLogModal(timer.timerId);
    this.onTimerStopped.emit(stoppedTimerData);
  }

  onDeleteTimer(timer: Timer, event: Event): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the timer for "${timer.taskName}"?`,
      header: 'Delete Timer',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.timerService.deleteTimer(timer.timerId).subscribe({
          next: () => this.toastr.success('Timer deleted successfully'),
          error: (err) => {
            if (err.status === 404 || err.error?.message === 'Timer not found') {
              this.toastr.success('Timer deleted successfully');
            } else {
              this.toastr.error(err.error?.message || 'Failed to delete timer');
            }
          },
        });
      },
    });
  }

  onClickTimer(timer: Timer): void {
    this.onTimerClick.emit(timer.taskId);
  }

  onNavigateToTask(timer: Timer, event: Event): void {
    event.stopPropagation();
    this.router.navigate([this.roleSlug, 'projects', 'details'], {
      queryParams: { id: timer.projectId, task: timer.taskNumber },
    });
  }

  onNavigateToProject(timer: Timer, event: Event): void {
    event.stopPropagation();
    this.router.navigate([this.roleSlug, 'projects', 'details'], {
      queryParams: { id: timer.projectId },
    });
  }

  formatTime(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${this.pad(h)}:${this.pad(m)}:${this.pad(s)}`;
  }

  private pad(num: number): string {
    return num.toString().padStart(2, '0');
  }

  get timerCount(): number { return this.timers.length; }
  get runningCount(): number { return this.timers.filter(t => t.status === 'RUNNING').length; }
  get pausedCount(): number { return this.timers.filter(t => t.status === 'PAUSED').length; }

  trackByTimerId(index: number, timer: Timer): string {
    return timer.timerId;
  }

  onEditTimer(timer: Timer, event: Event): void {
    event.stopPropagation();
    this.selectedTimer = timer;
    this.showEditTimerModal = true;
    this.cdr.markForCheck();
  }

  handleTimerUpdate(updateData: any): void {
    this.timerService.updateTimer(updateData.timerId, updateData).subscribe({
      next: () => {
        this.toastr.success('Timer updated successfully');
        this.showEditTimerModal = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.toastr.error(err.error?.error?.message || 'Failed to update timer');
      },
    });
  }

  loadViewMode(): void {
    const savedMode = localStorage.getItem(this.VIEW_MODE_KEY);
    if (savedMode === 'single' || savedMode === 'detailed') {
      this.viewMode = savedMode;
    }
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'single' ? 'detailed' : 'single';
    localStorage.setItem(this.VIEW_MODE_KEY, this.viewMode);
    this.cdr.markForCheck();
  }

  get displayedTimers(): Timer[] {
    if (this.viewMode === 'single' && this.timers.length > 0) {
      return [this.getSingleViewTimer()];
    }
    return this.timers;
  }

  getSingleViewTimer(): Timer {
    const running = this.timers.find(t => t.status === 'RUNNING');
    if (running) return running;
    return this.timers
      .filter(t => t.status === 'PAUSED')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
      || this.timers[0];
  }

  onDragStarted(): void {
    this.zone.runOutsideAngular(() => {
      this.isDragging = true;
    });
    this.cdr.detach();
  }

  onDragEnded(): void {
    this.zone.run(() => {
      this.isDragging = false;
      this.cdr.reattach();
      this.cdr.markForCheck();
    });
  }
}
